import type { Account } from '@adtrackify/at-tracking-event-types';
import type { QueryClient } from '@tanstack/react-query';

import * as Logger from '@/lib/monitoring/logger';
import { dashboardBootstrapKeys, hydrateBootstrapCaches } from '@/features/dashboard/hooks/use-dashboard-bootstrap';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import type { Role } from '@/services/internal-api/accounts/types/account-role-types';

interface PrefetchAccountDataParams {
  queryClient: QueryClient;
  account: Account;
  pixelId: string;
  roles: Role[];
  currentUserEmail?: string;
}

async function prefetchDashboardBootstrap(
  queryClient: QueryClient,
  pixelId: string
): Promise<void> {
  try {
    const data = await queryClient.fetchQuery({
      queryKey: dashboardBootstrapKeys.byPixel(pixelId),
      queryFn: async () => {
        const response = await AccountsApiService.getDashboardBootstrap(pixelId);
        return response.data;
      },
      staleTime: 5 * 60 * 1000,
    });
    hydrateBootstrapCaches(queryClient, pixelId, data);
  } catch (error) {
    Logger.warn('Dashboard bootstrap prefetch failed', {
      error,
      pixelId,
    });
  }
}

async function prefetchTeamMembers(
  queryClient: QueryClient,
  account: Account,
  roles: Role[],
  currentUserEmail: string
): Promise<void> {
  const userRoleEntry = roles.find(role => role.accountId === account.id);
  const currentUserRole = userRoleEntry?.role ?? null;
  const hasTeamAccess = currentUserRole === 'owner' || currentUserRole === 'admin';

  if (!hasTeamAccess) return;

  try {
    await queryClient.prefetchQuery({
      queryKey: ['teamMembers', account.id],
      queryFn: async () => {
        const response = await AccountsApiService.getTeamMembers(account.id);
        return response.data.users.map((apiUser: any) => {
          // Bucket type OK: mapping external API response to internal shape
          const firstName = apiUser.firstName ?? '';
          const lastName = apiUser.lastName ?? '';
          const fullName = `${firstName} ${lastName}`.trim();
          return {
            id: apiUser.userId,
            name: fullName || apiUser.email,
            email: apiUser.email,
            status: apiUser.status === 'active' ? 'active' : 'pending',
            role: apiUser.role,
            isCurrentUser: apiUser.email === currentUserEmail,
          };
        });
      },
      staleTime: 5 * 60 * 1000,
    });
  } catch (error) {
    Logger.warn('Team members prefetch failed', { error });
  }
}

export async function prefetchAccountData({
  queryClient,
  account,
  pixelId,
  roles,
  currentUserEmail,
}: PrefetchAccountDataParams): Promise<void> {
  Logger.debug('Prefetching account data', {
    accountId: account.id,
    pixelId,
  });

  await Promise.all([
    prefetchDashboardBootstrap(queryClient, pixelId),
    currentUserEmail
      ? prefetchTeamMembers(queryClient, account, roles, currentUserEmail)
      : Promise.resolve(),
  ]);
}
